import { debounce } from 'lodash';
import type { ChangeEvent } from 'react';
import { useCallback, useEffect, useMemo } from 'react';
import { useAppDispatch, useAppSelector } from '../../../app.store';
import {
  initScope,
  resetScope,
  selectFilterScope,
  setFilter,
  setKeyword,
  setPagination,
} from './filters.slice';
import type { UseFiltersOptions, UseFiltersReturn } from './filters.type';

export function useFiltersHook<T = Record<string, any>>(
  scope: string,
  options: UseFiltersOptions<T> = {},
): UseFiltersReturn<T> {
  const {
    defaultPage,
    defaultLimit,
    defaultFilters = {} as T,
    persistOnUnmount = false,
  } = options;

  const dispatch = useAppDispatch();

  // Select current scope state with memoization
  const scopeState = useAppSelector(selectFilterScope<T>(scope));

  // Initialize scope on mount
  useEffect(() => {
    const defaultInfo = { scope, defaultFilters, defaultPage, defaultLimit };
    dispatch(initScope(defaultInfo));

    return () => {
      if (!persistOnUnmount) {
        dispatch(resetScope(defaultInfo));
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Memoized state values with defaults
  const { page, limit, keyword, filters } = useMemo(
    () => ({
      page: scopeState?.page ?? 1,
      limit: scopeState?.limit ?? 10,
      keyword: scopeState?.keyword ?? '',
      filters: scopeState?.filters ?? ({} as T),
    }),
    [scopeState],
  );

  // Handlers with useCallback for performance
  const handlePagination = useCallback(
    (newPage: number, newLimit: number) => {
      dispatch(setPagination({ scope, page: newPage, limit: newLimit }));
    },
    [dispatch, scope],
  );

  // Debounced search handler - accepts React.ChangeEvent<HTMLInputElement>
  const handleSearch = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      // Create debounced function that dispatches the keyword update
      const debouncedSearch = debounce((keyword: string) => {
        dispatch(setKeyword({ scope, keyword, defaultPage }));
      }, 500);

      debouncedSearch(value);
    },
    [dispatch, scope, defaultPage],
  );

  const handleFilter = useCallback(
    <K extends keyof T>(key: K, value: T[K]) => {
      dispatch(setFilter({ scope, key: String(key), value, defaultPage }));
    },
    [dispatch, scope, defaultPage],
  );

  const reset = useCallback(() => {
    dispatch(resetScope({ scope }));
  }, [dispatch, scope]);

  const skip = (page - 1) * limit;

  return {
    // State
    page,
    skip,
    limit,
    keyword,
    filters,

    // Handlers
    reset,
    handleSearch,
    handleFilter,
    handlePagination,
  };
}

export default useFiltersHook;
